import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Badge, Banner, PageLoading, TextInput } from '../../components/ui';
import { STATUS_ACTION, STATUS_LABEL, STATUS_TONE, money, quantity } from './orderMeta';
import type { Order, OrderStatus } from '../../types';

/**
 * One order, in full.
 *
 * Every descriptive field here comes from the order's own lines, not
 * from a join against the catalogue — the name, unit, HSN and GST rate
 * are what they were when the rep booked it. That is what makes this a
 * commercial record rather than a view of today's product master.
 */
export default function OrderDetail({
  orderId, canAct, onClose, onChanged,
}: {
  orderId: string;
  canAct: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [dispatch, setDispatch] = useState({
    consignmentNo: '', transportMode: '', trackingUrl: '', transporterName: '',
  });

  const load = async () => {
    try {
      setOrder(await api.get<Order>(`/orders/${orderId}`));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load that order.');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [orderId]);

  async function move(status: OrderStatus) {
    if (status === 'cancelled' && !reason.trim()) {
      setCancelling(true);
      return;
    }
    // On dispatch, open the handoff-details panel first; the panel's own
    // button calls move('dispatched') again once dispatching is true.
    if (status === 'dispatched' && !dispatching) {
      setDispatching(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/status`, {
        status,
        reason: status === 'cancelled' ? reason.trim() : undefined,
        ...(status === 'dispatched' ? {
          consignmentNo: dispatch.consignmentNo.trim() || undefined,
          transportMode: dispatch.transportMode.trim() || undefined,
          trackingUrl: dispatch.trackingUrl.trim() || undefined,
          transporterName: dispatch.transporterName.trim() || undefined,
        } : {}),
      });
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}.`);
      setCancelling(false);
      setDispatching(false);
      setReason('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update that.');
    } finally {
      setBusy(false);
    }
  }

  const next = order?.nextStatuses ?? [];

  return (
    <Modal
      title={order ? order.orderNo : 'Order'}
      subtitle={order
        ? `${order.customer?.name ?? 'Unknown customer'} · ${order.orderDate}`
        : undefined}
      extraWide
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          <span className="grow" />
          {canAct && next.includes('cancelled') ? (
            <button type="button" className="btn btn-danger-ghost" disabled={busy}
              onClick={() => void move('cancelled')}>
              {STATUS_ACTION.cancelled}
            </button>
          ) : null}
          {canAct
            ? next.filter((s) => s !== 'cancelled').map((status) => (
              <button key={status} type="button" className="btn btn-primary" disabled={busy}
                onClick={() => void move(status)}>
                {STATUS_ACTION[status] ?? status}
              </button>
            ))
            : null}
        </>
      )}
    >
      {loading || !order ? <PageLoading rows={3} /> : (
        <div className="col gap-16">
          <div className="row gap-8 wrap">
            <Badge tone={STATUS_TONE[order.status]}>{STATUS_LABEL[order.status]}</Badge>
            {order.isOfflineSync ? <Badge tone="neutral">Booked offline</Badge> : null}
            {order.isInterstate ? <Badge tone="neutral">Interstate · IGST</Badge> : null}
            {order.visitId ? <Badge tone="neutral">Booked during a visit</Badge> : null}
          </div>

          {cancelling ? (
            <div className="card">
              <div className="card-pad col gap-8">
                <TextInput
                  label="Why is this being cancelled?"
                  value={reason}
                  autoFocus
                  hint="The rep who booked it will see this."
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="row gap-8">
                  <button type="button" className="btn btn-danger" disabled={busy || !reason.trim()}
                    onClick={() => void move('cancelled')}>
                    Cancel this order
                  </button>
                  <button type="button" className="btn btn-ghost"
                    onClick={() => { setCancelling(false); setReason(''); }}>
                    Keep it
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {order.status === 'cancelled' && order.cancelReason ? (
            <Banner kind="danger">
              <strong>Cancelled.</strong> {order.cancelReason}
            </Banner>
          ) : null}

          {dispatching ? (
            <div className="card">
              <div className="card-pad col gap-8">
                <strong>Dispatch details</strong>
                <span className="faint small">All optional — fill what applies to this consignment.</span>
                <TextInput label="Consignment / docket no." value={dispatch.consignmentNo}
                  onChange={(e) => setDispatch((d) => ({ ...d, consignmentNo: e.target.value }))} />
                <TextInput label="Transporter name" value={dispatch.transporterName}
                  onChange={(e) => setDispatch((d) => ({ ...d, transporterName: e.target.value }))} />
                <TextInput label="Mode of transport" hint="e.g. Road, Rail, Air, Courier"
                  value={dispatch.transportMode}
                  onChange={(e) => setDispatch((d) => ({ ...d, transportMode: e.target.value }))} />
                <TextInput label="Tracking URL (if any)" value={dispatch.trackingUrl}
                  onChange={(e) => setDispatch((d) => ({ ...d, trackingUrl: e.target.value }))} />
                <div className="row gap-8">
                  <button type="button" className="btn btn-primary" disabled={busy}
                    onClick={() => void move('dispatched')}>
                    Mark dispatched
                  </button>
                  <button type="button" className="btn btn-ghost"
                    onClick={() => setDispatching(false)}>
                    Back
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {(order.consignmentNo || order.transporterName || order.transportMode || order.trackingUrl) ? (
            <div className="kv">
              {order.consignmentNo ? (<><span>Consignment</span><span>{order.consignmentNo}</span></>) : null}
              {order.transporterName ? (<><span>Transporter</span><span>{order.transporterName}</span></>) : null}
              {order.transportMode ? (<><span>Mode</span><span>{order.transportMode}</span></>) : null}
              {order.trackingUrl ? (
                <>
                  <span>Tracking</span>
                  <span><a href={order.trackingUrl} target="_blank" rel="noreferrer">Track shipment</a></span>
                </>
              ) : null}
            </div>
          ) : null}

          <div className="kv">
            <span>Booked by</span>
            <span>{order.user?.name ?? '—'}</span>

            <span>Area</span>
            <span>{order.customer?.route ?? <span className="faint">—</span>}</span>

            <span>Price list</span>
            <span>{order.priceList?.name ?? <span className="faint">—</span>}</span>

            <span>Place of supply</span>
            <span>
              {order.placeOfSupply ?? <span className="faint">Not known</span>}
              {' '}
              <span className="faint small">
                ({order.isInterstate ? 'IGST' : 'CGST + SGST'})
              </span>
            </span>

            {order.expectedDeliveryDate ? (
              <>
                <span>Wanted by</span>
                <span>{order.expectedDeliveryDate}</span>
              </>
            ) : null}

            {order.note ? (
              <>
                <span>Note</span>
                <span>{order.note}</span>
              </>
            ) : null}
          </div>

          <div className="card">
            <div className="card-head">
              <h3>What was ordered</h3>
              <span className="small faint">
                As booked — later catalogue changes do not touch this
              </span>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>HSN</th>
                    <th className="num">Qty</th>
                    <th className="num">Rate</th>
                    <th className="num">Disc.</th>
                    <th className="num">Taxable</th>
                    <th className="num">GST</th>
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items ?? []).map((item) => (
                    <tr key={item.id}>
                      <td className="primary">
                        {item.name}
                        <div className="small faint mono">{item.code}</div>
                      </td>
                      <td className="mono small">{item.hsnCode ?? '—'}</td>
                      <td className="num nowrap">{quantity(item.quantity, item.unit)}</td>
                      <td className="num">{money(item.unitPrice)}</td>
                      <td className="num">
                        {item.discountPercent > 0
                          ? <span>{item.discountPercent}%<div className="small faint">
                            −{money(item.discountAmount)}</div></span>
                          : <span className="faint">—</span>}
                      </td>
                      <td className="num">{money(item.taxableAmount)}</td>
                      <td className="num">
                        {item.gstRate}%
                        <div className="small faint">
                          {money(item.cgstAmount + item.sgstAmount + item.igstAmount)}
                        </div>
                      </td>
                      <td className="num primary">{money(item.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="order-summary">
            {order.taxBreakdown?.length ? (
              <div className="card">
                <div className="card-head"><h3>Tax</h3></div>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th className="num">Rate</th>
                        <th className="num">Taxable</th>
                        <th className="num">{order.isInterstate ? 'IGST' : 'CGST'}</th>
                        {!order.isInterstate ? <th className="num">SGST</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {order.taxBreakdown.map((band) => (
                        <tr key={band.gstRate}>
                          <td className="num">{band.gstRate}%</td>
                          <td className="num">{money(Number(band.taxableAmount))}</td>
                          <td className="num">
                            {money(Number(order.isInterstate ? band.igstAmount : band.cgstAmount))}
                          </td>
                          {!order.isInterstate ? (
                            <td className="num">{money(Number(band.sgstAmount))}</td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="card">
              <div className="card-pad">
                <div className="totals">
                  <span>Subtotal</span><span>{money(order.subtotal)}</span>
                  {order.discountAmount > 0 ? (
                    <>
                      <span>Discount</span>
                      <span>−{money(order.discountAmount)}</span>
                    </>
                  ) : null}
                  <span>Taxable</span><span>{money(order.taxableAmount)}</span>
                  {order.isInterstate ? (
                    <><span>IGST</span><span>{money(order.igstAmount)}</span></>
                  ) : (
                    <>
                      <span>CGST</span><span>{money(order.cgstAmount)}</span>
                      <span>SGST</span><span>{money(order.sgstAmount)}</span>
                    </>
                  )}
                  <span className="grand">Total</span>
                  <span className="grand">{money(order.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          <Timeline order={order} />
        </div>
      )}
    </Modal>
  );
}

function Timeline({ order }: { order: Order }) {
  const steps = [
    { label: 'Booked', at: order.createdAt },
    { label: 'Confirmed', at: order.confirmedAt },
    { label: 'Dispatched', at: order.dispatchedAt },
    { label: 'Delivered', at: order.deliveredAt },
  ].filter((s, i) => i === 0 || s.at || order.status !== 'cancelled');

  return (
    <div className="timeline">
      {steps.map((step) => (
        <div key={step.label} className={`timeline-step${step.at ? ' done' : ''}`}>
          <span className="timeline-dot">
            {step.at ? <Icon name="check" size={12} /> : null}
          </span>
          <div>
            <div className="timeline-label">{step.label}</div>
            <div className="small faint">
              {step.at ? new Date(step.at).toLocaleString() : 'Not yet'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
